// @ts-nocheck
import style from './Note.module.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ADD_FREE_TEXT, GET_NOTE_CONTENT, UPDATE_NOTE_PART } from '@ax/graphql';
import { convertUTCtoLocalTime, debounce, formatTimeToShortAMPM } from '@/utils';
import { NOTE_SECTION } from '@/constants/note-section';
import useAnalytics from '@/hooks/useAnalytics.tsx';
import * as diff from 'diff';
import { format, isAfter } from 'date-fns';
import ComplaintTitle from '@/components/ComplaintTitle/index.tsx';
import {
  useAppSelector,
  noteHasPendingCareCuesResponses,
  useAppDispatch,
  icd10CodeSelected,
  icd10DropdownOpened,
} from '@ax/redux-store';
import { TemplateSelection, Code, NOTE_STATUS } from '@ax/constants';
import { UpdateNotePartVariables } from '@/constants/update-note-part-variables';

export interface Content {
  ComplaintId: number;
  ComplaintName: string;
  sentences: Sentence[];
  Section: string;
}

interface Sentence {
  Sentence: string;
  CreatedAt: string;
  Order: number;
  selection: string;
  Section: string;
  UpdatedAt: string;
}

interface NoteContentProps {
  data?: Content[];
  sectionName: string;
  readonly?: boolean;
}

export interface AddFreeTextVariables {
  noteId: string;
  Section: string;
  Content: string;
  SharedId: string;
  ActionSource: 'User';
  Order: number;
}

const getSectionLabel = (section: string) => {
  if (section === 'PROGRESS') {
    return 'RE-EVALUATION';
  } else if (section === 'INTERVAL_HISTORY') {
    return 'INTERVAL HISTORY';
  }

  return section;
};

const useDebouncedMutation = (mutationFn: any, debounceDelay: number) => {
  return useCallback(
    debounce(async (variables: any, noteContentData, analytics) => {
      try {
        const { data } = await mutationFn({ variables });
        const newSentence = data?.updateNotePart ? data.updateNotePart : data?.addFreeText;
        const oldSentence = data?.updateNotePart
          ? noteContentData.getNoteContent.find(
              (noteContent: any) => noteContent.selection === newSentence.selection,
            )
          : { Content: '' };

        const sentencesDiff = diff.diffArrays(
          oldSentence.Content.replace(/[^\w\s]/gi, '')
            // create array of words from sentence
            .trim()
            .split(' ')
            // remove empty strings from all items in the array
            .map((x: string) => x.trim().replaceAll(' ', ''))
            .filter((x: string) => x !== ''),
          newSentence.Content.replace(/[^\w\s]/gi, '')
            // create array of words from sentence
            .trim()
            .split(' ')
            // remove empty strings from all items in the array
            .map((x: string) => x.trim().replaceAll(' ', ''))
            .filter((x: string) => x !== ''),
        );

        const wordsAdded = sentencesDiff
          .filter((d) => d.added)
          .reduce((acc, curr) => (curr.count ?? 0) + acc, 0);
        const wordsRemoved = sentencesDiff
          .filter((d) => d.removed)
          .reduce((acc, curr) => (curr.count ?? 0) + acc, 0);

        if (wordsAdded === 0 && wordsRemoved === 0) {
          return;
        }

        analytics.onNoteEdit({
          noteSection: getSectionLabel(variables.Section),
          noteId: variables.noteId,
          eventDateTime: format(new Date(), 'MM/dd/yyyy HH:mm:ss'),
          wordsAdded,
          wordsRemoved,
        });

        return true;
      } catch (error: any) {
        console.error('Mutation error:', error);
        if (error.networkError) console.error('Network error:', error.networkError);
        if (error.graphQLErrors)
          error.graphQLErrors.forEach((graphQLError: any) =>
            console.error('GraphQL error:', graphQLError),
          );
        return false;
      }
    }, debounceDelay),
    [mutationFn, debounceDelay],
  );
};

const NoteContent = ({ data, sectionName, readonly }: NoteContentProps) => {
  const dispatch = useAppDispatch();
  const analytics = useAnalytics();
  const complaints = useAppSelector((state) => state.note.templates);
  const selectedVisitDate = useAppSelector((state) => state.selectedDate);
  const visitsState = useAppSelector((state) => state.selectedVisit);
  const [addFreeText] = useMutation<{ addFreeText: any }, AddFreeTextVariables>(ADD_FREE_TEXT);
  const [updateNotePart] = useMutation<{ updateNotePart: any }, UpdateNotePartVariables>(
    UPDATE_NOTE_PART,
  );
  const [previousDataLength, setPreviousDataLength] = useState(data ? data.length : 0);
  const paragraphRefs = useRef<(HTMLParagraphElement | null)[]>([]);
  const noteId = visitsState.uuid;
  const { data: noteContentData } = useQuery(GET_NOTE_CONTENT, {
    variables: { noteId, contentOptions: { noteId } },
  });
  const hasPendingCareCuesResponses = useAppSelector(
    noteHasPendingCareCuesResponses(visitsState.uuid),
  );

  useEffect(() => {
    if (noteContentData && noteContentData?.getNoteContent?.length > previousDataLength) {
      setPreviousDataLength(noteContentData?.getNoteContent?.length);
      paragraphRefs.current.forEach((paragraph) => {
        if (paragraph) paragraph.innerText = '';
      });
    }
  }, [noteContentData, previousDataLength]);

  const debouncedHandleAddFreeText = useDebouncedMutation(addFreeText, 100);
  const debouncedHandleUpdateNotePart = useDebouncedMutation(updateNotePart, 100);

  const handleInput = (
    e: React.FormEvent<HTMLParagraphElement>,
    handler: any,
    variables: any,
    isEdit: boolean,
  ) => {
    const content = e.currentTarget.innerText.replace(/(?:\r\n|\r|\n)/g, '\n');
    if (content || isEdit) {
      handler(
        { ...variables, Content: content, Sentence: content },
        noteContentData,
        analytics,
        selectedVisitDate,
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      const selection = window.getSelection();
      const range = selection?.getRangeAt(0);
      const textNode = document.createTextNode('\n');
      range?.insertNode(textNode);
      range?.setStartAfter(textNode);
      range?.collapse(true);
    }
  };

  const findComplaint = (data: Content) => {
    return complaints?.find(
      (c: TemplateSelection) => c.section === sectionName && c.complaintId === data.ComplaintId,
    );
  };

  const isUploadedToMds = visitsState.noteStatus === NOTE_STATUS.NOTE_UPLOADED_TO_MDS;
  const isUploadedToEhr = visitsState.noteStatus === NOTE_STATUS.NOTE_UPLOADED_TO_EHR;

  return (
    <>
      {data?.map((d, index) => (
        <div
          className={style.note__content}
          key={`${visitsState.uuid}-${d.ComplaintId}-${index}`}
        >
          {d.ComplaintName && (
            <div className={style.note__content__title}>
              {findComplaint(d) && (
                <ComplaintTitle
                  complaint={findComplaint(d)}
                  onValueChanged={(newCodeSelection, previousCode) => {
                    const complaint = findComplaint(d);
                    const newCode = complaint.codes.find(
                      (c: Code) => c.code === newCodeSelection?.code,
                    );
                    const currentSelection =
                      complaint.codes.find((c: Code) => c.code === previousCode?.code) || null;
                    if (currentSelection?.code === newCode?.code) {
                      return;
                    }
                    dispatch(
                      icd10CodeSelected({
                        currentSelection,
                        newCode,
                        noteId,
                        templateId: d.ComplaintId,
                        templateName: d.ComplaintName,
                        section: sectionName,
                      }),
                    );
                  }}
                  onDropdownOpen={() => {
                    dispatch(
                      icd10DropdownOpened({
                        noteId,
                        templateName: d.ComplaintName,
                        section: sectionName,
                      }),
                    );
                  }}
                />
              )}
            </div>
          )}
          {d.sentences.map((sentence, i) => (
            <div key={i}>
              {sentence.Section === NOTE_SECTION.progress && (
                <div>
                  <strong>Progress #{sentence.Order}</strong>
                  <br />
                  <strong>
                    Time {formatTimeToShortAMPM(convertUTCtoLocalTime(sentence.CreatedAt))}
                  </strong>
                </div>
              )}
              <p
                onBlur={(e) =>
                  handleInput(
                    e,
                    debouncedHandleUpdateNotePart,
                    {
                      noteId: visitsState.uuid,
                      SharedId: visitsState.uuid,
                      selection: sentence.selection,
                      Section: sectionName,
                      ActionSource: 'User',
                      UpdatedAt: new Date().toISOString(),
                    },
                    true,
                  )
                }
                onKeyDown={handleKeyDown}
                contentEditable={
                  !readonly &&
                  ((!hasPendingCareCuesResponses &&
                    isAfter(
                      new Date(sentence.UpdatedAt),
                      new Date(visitsState?.latestNoteStatus?.createdAt),
                    )) ||
                    (!isUploadedToMds && !isUploadedToEhr))
                }
                suppressContentEditableWarning
                className={style.note__content__text}
                dangerouslySetInnerHTML={{ __html: sentence.Sentence.replace(/\n/g, '<br>') }}
              />
            </div>
          ))}
          {d.ComplaintName && (
            <p
              onBlur={(e) => {
                handleInput(
                  e,
                  debouncedHandleAddFreeText,
                  {
                    noteId: visitsState.uuid,
                    SharedId: visitsState.uuid,
                    Section: sectionName,
                    ComplaintId: d.ComplaintId,
                    ActionSource: 'User',
                    Order: d.sentences && d.sentences.length > 0 ? d.sentences.length + 1 : 1,
                  },
                  false,
                );
              }}
              contentEditable={!isUploadedToMds && !isUploadedToEhr && !readonly}
              className={style.note__content__text}
              suppressContentEditableWarning
              ref={(el) => (paragraphRefs.current[index] = el)}
            />
          )}
        </div>
      ))}
      {visitsState.uuid && (
        <div
          className={style.note__content}
          key={`${visitsState.uuid}-free-text`}
        >
          <p
            onBlur={(e) => {
              handleInput(
                e,
                debouncedHandleAddFreeText,
                {
                  noteId: visitsState.uuid,
                  SharedId: visitsState.uuid,
                  Section: sectionName,
                  ActionSource: 'User',
                  Order: data && data.length > 0 ? data[data.length - 1]?.sentences.length + 1 : 1,
                },
                false,
              );
            }}
            contentEditable={
              (!isUploadedToMds && !isUploadedToEhr && !readonly) ||
              sectionName === NOTE_SECTION.progress
            }
            className={style.note__content__text}
            suppressContentEditableWarning
            ref={(el) => (paragraphRefs.current[data ? data.length : 0] = el)}
          />
        </div>
      )}
    </>
  );
};

export default NoteContent;
